import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';

type Tone = 'success' | 'error' | 'info';
interface Toast { id: number; tone: Tone; message: string; }
interface Ctx {
  toasts: Toast[];
  push: (t: { tone: Tone; message: string }) => void;
  dismiss: (id: number) => void;
}

const ToastCtx = createContext<Ctx | null>(null);

let nextId = 1;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const dismiss = useCallback((id: number) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);
  const push = useCallback((t: { tone: Tone; message: string }) => {
    const id = nextId++;
    setToasts(prev => [...prev, { id, ...t }]);
    setTimeout(() => dismiss(id), 5000);
  }, [dismiss]);
  const value = useMemo<Ctx>(() => ({ toasts, push, dismiss }), [toasts, push, dismiss]);
  return <ToastCtx.Provider value={value}>{children}</ToastCtx.Provider>;
}

export function useToast() {
  const ctx = useContext(ToastCtx);
  if (!ctx) throw new Error('useToast outside ToastProvider');
  return ctx;
}
